import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData, VerifyData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import {
  Add, Edit, Visibility, Delete, Inventory2, FilterList, Refresh, CheckCircle, Cancel, Close
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box
} from '@mui/material';
import StockTransferOutModal from './StockTransferOutModal';
import './StockTransferOutIndex.css';

const StockTransferOutIndex = () => {
  // Session validation
  useSessionValidation();

  const history = useHistory();
  const { companyId, branchId, token, userId, permissions } = useAuth();
  const { canCreateTransaction, canEditTransaction, canDeleteTransaction } = usePermissions();

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [stockTransfers, setStockTransfers] = useState([]);
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [transferDetails, setTransferDetails] = useState([]);
  const [originalTransferDetails, setOriginalTransferDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
    }
  }, [companyId, token, fromDate, toDate]);

  // Fetch functions
  const fetchStockTransfers = async () => {
    try {
      const activeResponse = await fetchData('StockTransferApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));

      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('StockTransferApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive stock transfers:', inactiveError);
      }

      setStockTransfers([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching stock transfers:', error);
      setStockTransfers([]);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('SaleApi', `/ItemStockListDetails/${companyId}/${branchId}`, token);
      setItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await fetchData('BranchApi', `/${companyId}`, token);
      const branchData = response.response_data || response || [];
      // Filter out the current branch (branchId) from the list
      // Convert both to numbers for proper comparison since branchId might be a string
      const filteredBranches = branchData.filter(branch => parseInt(branch.Id) !== parseInt(branchId));
      setBranches(filteredBranches);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
  };

  const fetchMaxTransferNo = async () => {
    try {
      const response = await fetchData('StockTransferApi', `/MaxTransferNo/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || {};
      const StockTransferNumber = data.BillNumber || data.BillNumber || 1;
      const StockTransferPrefix = data.BillPrefix || data.BillPrefix || 'STO';
      const finYear = data.FinYear || data.finYear || '';

      return { StockTransferNumber, StockTransferPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max transfer number:', error);
      return { StockTransferNumber: 1, StockTransferPrefix: 'STO', finYear: '' };
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Loading stock transfers...');
    try {
      await Promise.allSettled([
        fetchStockTransfers(),
        fetchItems(),
        fetchBranches()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleStockTransfer = async (transferId) => {
    try {
      const response = await fetchData('StockTransferApi', `/${transferId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching single stock transfer:', error);
      return null;
    }
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setModalLoading(true);
      const freshData = await fetchSingleStockTransfer(itemId);

      if (freshData) {
        const transferData = freshData.StockTransferOut || freshData;
        const details = freshData.StockTransferOutDetails || [];

        setSelectedItem(transferData);
        setFormData({
          Id: transferData.Id,
          CompanyId: transferData.CompanyId || companyId,
          FromBranchId: branchId,
          ToBranchId: transferData.ToBranchId,
          StockTransferNumber: transferData.StockTransferNumber,
          StockTransferPrefix: transferData.StockTransferPrefix || '',
          FinYear: transferData.FinYear || '',
          StockTransferDate: transferData.StockTransferDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          TotalQuantity: transferData.TotalQuantity || 0,
          TotalAmount: transferData.TotalAmount || 0,
          TotalTaxableAmount: transferData.TaxableAmount || transferData.TotalAmount || 0,
          TotalCGSTAmount: transferData.CGSTAmount || 0,
          TotalSGSTAmount: transferData.SGSTAmount || 0,
          TotalIGSTAmount: transferData.IGSTAmount || 0,
          TotalWithGST: transferData.GrandTotal || transferData.TotalAmount || 0,
          RoundOff: transferData.RoundOff || 0,
          GrandTotal: transferData.GrandTotal || transferData.TotalAmount || 0,
          Remarks: transferData.Remarks || '',
          TransporterName: transferData.TransporterName || '',
          TransporterGST: transferData.TransporterGST || '',
          VehicleNumber: transferData.VehicleNumber || '',
          Distance: transferData.Distance || '',
          IsVerified: transferData.IsVerified || false,
          IsActive: transferData.IsActive ?? true,
          CreatedBy: transferData.CreatedBy,
          CreatedDate: transferData.CreatedDate,
          CreatedByName: transferData.CreatedByName,
          UpdatedBy: transferData.ModifiedBy,
          UpdatedDate: transferData.ModifiedDate,
          UpdatedByName: transferData.ModifiedByName
        });
        const mappedDetails = details.map(d => ({
          Id: d.Id || 0,
          ItemId: d.ItemId,
          HSNCode: d.HSNCode || '',
          Quantity: d.Quantity || 0,
          Rate: d.Rate || 0,
          GSTRate: d.GSTRate || 0,
          Amount: d.Amount || 0,
          TaxableAmount: d.TaxableAmount || d.Amount || 0,
          CGSTAmount: d.CGSTAmount || 0,
          SGSTAmount: d.SGSTAmount || 0,
          IGSTAmount: d.IGSTAmount || 0,
          TotalAmount: d.TotalAmount || d.Amount || 0,
          IsActive: d.IsActive ?? true,
          CreatedBy: d.CreatedBy,
          CreatedDate: d.CreatedDate,
          UpdatedBy: d.ModifiedBy,
          UpdatedDate: d.ModifiedDate
        }));
        
        setTransferDetails(mappedDetails);
        // Store original details for balance calculations in edit mode
        setOriginalTransferDetails([...mappedDetails]);
      }
      setModalLoading(false);
    } else {
      // Create mode
      setModalLoading(true);
      const maxTransferData = await fetchMaxTransferNo();

      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        ToBranchId: '',
        StockTransferNumber: maxTransferData.StockTransferNumber,
        StockTransferPrefix: maxTransferData.StockTransferPrefix,
        FinYear: maxTransferData.finYear,
        StockTransferDate: new Date().toISOString().split('T')[0],
        TotalQuantity: 0,
        TotalAmount: 0,
        TotalTaxableAmount: 0,
        TotalCGSTAmount: 0,
        TotalSGSTAmount: 0,
        TotalIGSTAmount: 0,
        TotalWithGST: 0,
        RoundOff: 0,
        GrandTotal: 0,
        Remarks: '',
        TransporterName: '',
        TransporterGST: '',
        VehicleNumber: '',
        Distance: '',
        IsVerified: false,
        IsActive: true
      });
      setTransferDetails([]);
      setOriginalTransferDetails([]);
      setModalLoading(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setTransferDetails([]);
    setOriginalTransferDetails([]);
  };

  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} stock transfer...`);

      // Helper function to round to 2 decimal places
      const roundTo2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

      const dataToSend = {
        StockTransfer: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          FromBranchId: formData.BranchId || branchId,
          ToBranchId: parseInt(formData.ToBranchId) || 0,
          StockTransferNumber: formData.StockTransferNumber || '',
          StockTransferPrefix: formData.StockTransferPrefix || '',
          FinYear: formData.FinYear || '',
          StockTransferDate: formData.StockTransferDate || new Date().toISOString().split('T')[0],
          TotalQuantity: roundTo2(parseFloat(formData.TotalQuantity) || 0),
          TotalAmount: roundTo2(parseFloat(formData.TotalAmount) || 0),
          TaxableAmount: roundTo2(parseFloat(formData.TotalTaxableAmount) || 0),
          CGSTAmount: roundTo2(parseFloat(formData.TotalCGSTAmount) || 0),
          SGSTAmount: roundTo2(parseFloat(formData.TotalSGSTAmount) || 0),
          IGSTAmount: roundTo2(parseFloat(formData.TotalIGSTAmount) || 0),
          RoundOff: roundTo2(parseFloat(formData.RoundOff) || 0),
          GrandTotal: Math.round(parseFloat(formData.GrandTotal) || 0),
          Remarks: formData.Remarks || '',
          TransporterName: formData.TransporterName || '',
          TransporterGST: formData.TransporterGST || '',
          VehicleNumber: formData.VehicleNumber || '',
          Distance: formData.Distance ? parseInt(formData.Distance) : null,
          IsVerified: formData.IsVerified || false,
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          CreatedDate: modalAction === 'create' ? null : formData.CreatedDate,
          UpdatedBy: modalAction === 'edit' ? userId : null
        },
        StockTransferDetails: transferDetails.map(detail => ({
          Id: modalAction === 'edit' ? (detail.Id || 0) : 0,
          CompanyId: companyId,
          StockTransferOutId: modalAction === 'edit' ? formData.Id : 0,
          ItemId: parseInt(detail.ItemId) || 0,
          HSNCode: detail.HSNCode || '',
          Quantity: roundTo2(parseFloat(detail.Quantity) || 0),
          Rate: roundTo2(parseFloat(detail.Rate) || 0),
          Amount: roundTo2(parseFloat(detail.Amount) || 0),
          GSTRate: roundTo2(parseFloat(detail.GSTRate) || 0),
          TaxableAmount: roundTo2(parseFloat(detail.TaxableAmount) || 0),
          CGSTAmount: roundTo2(parseFloat(detail.CGSTAmount) || 0),
          SGSTAmount: roundTo2(parseFloat(detail.SGSTAmount) || 0),
          IGSTAmount: roundTo2(parseFloat(detail.IGSTAmount) || 0),
          TotalAmount: roundTo2(parseFloat(detail.TotalAmount) || 0),
          IsActive: detail.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : detail.CreatedBy,
          CreatedDate: modalAction === 'create' ? null : detail.CreatedDate,
          UpdatedBy: modalAction === 'edit' ? userId : null
        }))
      };

      if (modalAction === 'create') {
        await postData('StockTransferApi', '', dataToSend, token);
        setSuccess('Stock Transfer Out created successfully!');
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Stock Transfer ID is missing');
        await updateData('StockTransferApi', formData.Id, dataToSend, token);
        setSuccess('Stock Transfer Out updated successfully!');
      }

      handleCloseModal();
      await fetchStockTransfers();
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} stock transfer. Please try again.`);
      setLoading(false);
    }
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, transferNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(transferNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;

    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('StockTransferApi', deleteItemId, token);
      setSuccess('Stock Transfer Out deleted successfully!');
      await fetchStockTransfers();
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete stock transfer.');
    }
    setLoading(false);
  };

  // Activate handlers
  const handleOpenActivateModal = (id, transferNo) => {
    setActivateItemId(id);
    setActivateItemNo(transferNo);
    setActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setActivateItemId(null);
    setActivateItemNo('');
  };

  const handleActivate = async () => {
    if (!activateItemId) return;

    setLoading(true);
    handleCloseActivateModal();
    try {
      await activateData('StockTransferApi', activateItemId, token);
      setSuccess('Stock Transfer Out activated successfully!');
      await fetchStockTransfers();
    } catch (error) {
      console.error('Error activating:', error);
      setError('Failed to activate stock transfer.');
    }
    setLoading(false);
  };

  // Filter and pagination
  const getFilteredData = (data) => {
    if (!Array.isArray(data)) return [];

    let filtered = [...data];

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.StockTransferNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branches.find(b => b.Id === item.ToBranchId)?.Name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(item =>
        filterStatus === 'active' ? item.IsActive : !item.IsActive
      );
    }

    if (filterVerified !== 'all') {
      filtered = filtered.filter(item =>
        filterVerified === 'verified' ? item.IsVerified : !item.IsVerified
      );
    }

    return filtered;
  };

  const getPaginatedData = (data) => {
    const filtered = getFilteredData(data) || [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); };
  const handleRefresh = () => fetchAllData();
  const handleCloseSnackbar = () => { setError(''); setSuccess(''); };

  // Date change handlers
  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setCurrentPage(1);
  };

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData(stockTransfers);

  return (
    <div className="stock-transfer-out-dashboard">
      {loading && <LoadingOverlay message={loadingMessage} />}

      <div className="dashboard-header">
        <h5 className="plain-header">Stock Transfer Out Management</h5>
      </div>

      <div className="dashboard-content">
        <div className="data-table-container">
          <div className="table-header">
            <div className="search-filter-section">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search by Transfer No, Branch..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </div>

              <div className="date-filter">
                <input
                  type="date"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  title="From Date"
                />
                <input
                  type="date"
                  value={toDate}
                  onChange={handleToDateChange}
                  title="To Date"
                />
              </div>

              <div className="filter-section">
                <FilterList className="filter-icon" />
                <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="filter-section">
                <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All</option>
                  <option value="verified">Verified</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              <Button variant="outlined" onClick={handleRefresh} disabled={loading} className="refresh-button desktop-only" startIcon={<Refresh />}>
                Refresh
              </Button>
              {canCreateTransaction && (
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal('create')} className="add-button desktop-only">
                  Add Transfer Out
                </Button>
              )}

              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} disabled={loading} className="refresh-icon-btn mobile-only">
                  <Refresh />
                </IconButton>
              </Tooltip>
              {canCreateTransaction && (
                <Tooltip title="Add Transfer Out">
                  <IconButton onClick={() => handleOpenModal('create')} className="add-icon-btn mobile-only">
                    <Add />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>

          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Sl No.</th>
                  <th>Transfer No.</th>
                  <th>Date</th>
                  <th>To Branch</th>
                  <th>Total Qty</th>
                  <th>Total Amount</th>
                  <th>Verified</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => (
                  <tr key={item.Id}>
                    <td>{startIndex + index + 1}</td>
                    <td>
                      <strong>
                        {item.StockTransferPrefix && item.FinYear
                          ? `${item.StockTransferPrefix}/${item.StockTransferNumber}/${item.FinYear}`
                          : item.StockTransferNumber || '-'}
                      </strong>
                    </td>
                    <td>{formatDate(item.StockTransferDate)}</td>
                    <td>{branches.find(b => b.Id === item.ToBranchId)?.Name || '-'}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{item.TotalQuantity || 0}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>{formatCurrency(item.GrandTotal || item.TotalAmount)}</td>
                    <td>
                      <Chip
                        label={item.IsVerified ? 'Verified' : 'Pending'}
                        color={item.IsVerified ? 'success' : 'warning'}
                        size="small"
                      />
                    </td>
                    <td>
                      <Chip
                        label={item.IsActive ? 'Active' : 'Inactive'}
                        color={item.IsActive ? 'success' : 'default'}
                        size="small"
                      />
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleOpenModal('view', item)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canEditTransaction && item.IsActive && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenModal('edit', item)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDeleteTransaction && (
                          item.IsActive ? (
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleOpenDeleteModal(item.Id, item.StockTransferNumber)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Activate">
                              <IconButton size="small" onClick={() => handleOpenActivateModal(item.Id, item.StockTransferNumber)}>
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {paginatedData.length === 0 && (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                      No stock transfers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-section">
            <div className="pagination-info">
              <span>Showing {startIndex + 1} to {Math.min(startIndex + rowsPerPage, totalCount)} of {totalCount} entries</span>
              <div className="rows-per-page">
                <label>Show:</label>
                <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                First
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                return (
                  <button
                    key={page}
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stock Transfer Out Modal */}
      {openModal && (
        <StockTransferOutModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          transferDetails={transferDetails}
          setTransferDetails={setTransferDetails}
          originalTransferDetails={originalTransferDetails}
          onSave={handleSave}
          items={items}
          branches={branches}
          loading={modalLoading}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#fef2f2', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Delete sx={{ color: '#dc2626', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Delete Stock Transfer</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to delete stock transfer <strong>{deleteItemNo}</strong>?
          </Typography>
          <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ borderColor: '#d1d5db', color: '#6b7280' }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Modal */}
      <Dialog open={activateModalOpen} onClose={handleCloseActivateModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#f0fdf4', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle sx={{ color: '#16a34a', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Activate Stock Transfer</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to activate stock transfer <strong>{activateItemNo}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseActivateModal} variant="outlined" sx={{ borderColor: '#d1d5db', color: '#6b7280' }}>Cancel</Button>
          <Button onClick={handleActivate} variant="contained" sx={{ backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}>Activate</Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
    </div>
  );
};

export default StockTransferOutIndex;
